/**
 * Proveedores — la ficha fiscal de cada tercero que nos factura.
 *
 * Esta pantalla existe por un motivo concreto: los ficheros del 347 y del 190
 * exigen por tercero datos que no están en ninguna factura (el código de
 * provincia de dos dígitos, el país, el NIF-IVA, la marca de inversión del
 * sujeto pasivo, la subclave de retención) y hasta ahora no había dónde ponerlos.
 *
 * Las fichas NO se dan de alta a mano: nacen solas con el primer gasto del
 * proveedor. Lo que se hace aquí es COMPLETARLAS, y por eso la columna que manda
 * es «qué falta» y el filtro por defecto puede dejar solo las incompletas.
 */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertTriangle,
  Building2,
  Check,
  ChevronRight,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
} from 'lucide-react'
import {
  AEAT_PROVINCES,
  AEAT_PROVINCE_NON_RESIDENT,
  MODEL_190_KEYS,
  provinceName,
  subkeysForKey,
  type SupplierDto,
  type SupplierGapCode,
  type SupplierListResponse,
} from '@jr2os/shared'
import { api, apiErrorMessage, ApiError } from '../lib/api'
import { useAuth } from '../contexts/AuthContext'
import Modal from '../components/ui/Modal'
import { confirmDialog, toast } from '../lib/dialogs'
import { formatCents } from '../lib/money'
import { formatDate } from '../lib/formatting'
import { useTableSort } from '../lib/useTableSort'

/** Color de la etiqueta de cada falta: el NIF es lo más grave. */
const GAP_TONE: Record<SupplierGapCode, string> = {
  NIF: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
  PROVINCIA: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  // En rojo como el NIF: no es un hueco, es un dato que se contradice.
  PROVINCIA_INCOHERENTE: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
  NIF_IVA: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300',
  NOMBRE_PARTIDO: 'bg-violet-500/15 text-violet-700 dark:text-violet-300',
  SUBCLAVE: 'bg-violet-500/15 text-violet-700 dark:text-violet-300',
}

/**
 * Provincias que están FUERA del territorio de aplicación del impuesto: Las
 * Palmas, Santa Cruz de Tenerife, La Palma, Ceuta y Melilla (art. 3.Dos.1.º
 * LIVA). Un proveedor de ahí tiene NIF español y provincia válida, así que nada
 * delata que no está establecido en el TAI.
 */
const FUERA_DEL_TAI = ['35', '38', '53', '51', '52']

const inputCls =
  'w-full px-3 py-2 rounded-lg text-sm t-primary bg-[var(--bg-input)] border border-base focus:outline-none focus:ring-2 focus:ring-amber-500/40'

export default function Suppliers() {
  const { t } = useTranslation(['suppliers', 'common'])
  const auth = useAuth()
  const canWrite =
    auth.status === 'authenticated' &&
    (auth.user.role === 'ADMIN' || auth.user.role === 'CONSULTOR')

  const [data, setData] = useState<SupplierListResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [onlyIncomplete, setOnlyIncomplete] = useState(false)
  const [includeInactive, setIncludeInactive] = useState(false)
  const [editing, setEditing] = useState<SupplierDto | null>(null)
  const [creating, setCreating] = useState(false)
  const [rebuilding, setRebuilding] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (q.trim()) params.set('q', q.trim())
      if (onlyIncomplete) params.set('incomplete', 'true')
      if (includeInactive) params.set('inactive', 'true')
      const resp = await api.get<SupplierListResponse>(`/suppliers?${params.toString()}`)
      setData(resp)
    } catch (err) {
      setError(apiErrorMessage(err, t('errors.load')))
    } finally {
      setLoading(false)
    }
  }, [q, onlyIncomplete, includeInactive, t])

  useEffect(() => {
    void reload()
  }, [reload])

  async function rebuild() {
    setRebuilding(true)
    try {
      const r = await api.post<{ suppliersCreated: number; expensesLinked: number }>(
        '/suppliers/rebuild',
        {}
      )
      toast.success(
        t('rebuild.done', { suppliers: r.suppliersCreated, expenses: r.expensesLinked })
      )
      await reload()
    } catch (err) {
      toast.error(apiErrorMessage(err, t('errors.rebuild')))
    } finally {
      setRebuilding(false)
    }
  }

  // Orden por columna (en el navegador: la lista llega entera, sin paginar). Sin orden elegido, el de siempre (activas primero y por
  // nombre). «Qué falta» ordena por cuántas faltas tiene la ficha; el NIF vacío va siempre al final.
  const { rows: items, header } = useTableSort(data?.items, {
    name: (s) => s.name,
    nif: (s) => s.nif,
    where: (s) => (s.provinceCode ? `${s.country} · ${provinceName(s.provinceCode)}` : s.country),
    invoices: (s) => s.expenseCount,
    base: (s) => s.baseCents,
    gaps: (s) => s.gaps.length,
  })

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <Building2 size={20} className="text-amber-700 dark:text-amber-300" />
            {t('title')}
          </h1>
          <p className="text-xs t-muted">{t('subtitle')}</p>
        </div>
        {canWrite && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void rebuild()}
              disabled={rebuilding}
              className="px-3 py-1.5 rounded-lg text-sm font-medium t-secondary border border-base bg-hover flex items-center gap-1.5 disabled:opacity-50"
              title={t('rebuild.help')}
            >
              <RefreshCw size={14} className={rebuilding ? 'animate-spin' : ''} />
              {t('rebuild.button')}
            </button>
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-amber-600 via-amber-500 to-amber-700 hover:shadow-glow-amber flex items-center gap-1.5"
            >
              <Plus size={14} /> {t('newButton')}
            </button>
          </div>
        )}
      </header>

      {/* Por qué esta pantalla existe. No es decoración: quien entra aquí la
          primera vez no sabe que el 347 necesita un código de provincia. */}
      {data && data.incompleteCount > 0 && (
        <div className="surface p-3 flex items-start gap-2 text-xs border-l-4 border-amber-500">
          <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
          <p className="t-secondary leading-relaxed">
            {t('banner.incomplete', { count: data.incompleteCount })}
          </p>
        </div>
      )}

      {data && data.orphanExpenseCount > 0 && (
        <div className="surface p-3 flex items-start gap-2 text-xs border-l-4 border-rose-500">
          <AlertTriangle size={16} className="text-rose-600 shrink-0 mt-0.5" />
          <p className="t-secondary leading-relaxed">
            {t('banner.orphans', { count: data.orphanExpenseCount })}
          </p>
        </div>
      )}

      <div className="surface p-3 flex items-end gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 t-faint" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('filters.search')}
            className={`${inputCls} pl-8`}
          />
        </div>
        <label className="flex items-center gap-2 text-xs t-secondary">
          <input
            type="checkbox"
            checked={onlyIncomplete}
            onChange={(e) => setOnlyIncomplete(e.target.checked)}
          />
          {t('filters.onlyIncomplete')}
        </label>
        <label className="flex items-center gap-2 text-xs t-secondary">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          {t('filters.includeInactive')}
        </label>
      </div>

      {error && <div className="surface p-3 text-sm text-rose-600">{error}</div>}

      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs t-muted border-b border-base">
              {header('name', t('table.name'), { caps: false })}
              {header('nif', t('table.nif'), { caps: false })}
              {header('where', t('table.where'), { caps: false })}
              {header('invoices', t('table.invoices'), { align: 'right', caps: false })}
              {header('base', t('table.base'), { align: 'right', caps: false })}
              {header('gaps', t('table.gaps'), { caps: false })}
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr
                key={s.id}
                className="border-b border-base last:border-0 bg-hover cursor-pointer"
                onClick={() => setEditing(s)}
              >
                <td className="px-3 py-2">
                  <div className="t-primary font-medium flex items-center gap-1.5">
                    {s.name}
                    {s.reviewedAt && (
                      <span
                        title={t('table.reviewedAt', {
                          date: formatDate(s.reviewedAt),
                          who: s.reviewedByName ?? '',
                        })}
                      >
                        <ShieldCheck size={13} className="text-emerald-600" />
                      </span>
                    )}
                    {s.possibleDuplicateOf && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-orange-500/15 text-orange-700 dark:text-orange-300"
                        title={t('table.duplicateHelp', { otra: s.possibleDuplicateOf })}
                      >
                        {t('table.duplicate')}
                      </span>
                    )}
                    {!s.active && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-input-soft)] t-faint">
                        {t('table.inactive')}
                      </span>
                    )}
                  </div>
                  {s.lastExpenseDate && (
                    <div className="text-[11px] t-faint">
                      {t('table.lastInvoice', { date: formatDate(s.lastExpenseDate) })}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 t-secondary font-mono text-xs">
                  {s.nif ?? <span className="t-faint">—</span>}
                  {s.vatNumber && s.vatNumber !== s.nif && (
                    <div className="t-faint">{s.vatNumber}</div>
                  )}
                </td>
                <td className="px-3 py-2 t-secondary text-xs">
                  {s.country}
                  {s.provinceCode ? ` · ${provinceName(s.provinceCode)}` : ''}
                  {!s.establishedInTai && (
                    <div className="text-[11px] text-cyan-700 dark:text-cyan-300">
                      {t('table.notEstablished')}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-right t-secondary">{s.expenseCount}</td>
                <td className="px-3 py-2 text-right t-secondary">{formatCents(s.baseCents)}</td>
                <td className="px-3 py-2">
                  {s.gaps.length === 0 ? (
                    <span className="text-xs text-emerald-700 dark:text-emerald-300 flex items-center gap-1">
                      <Check size={13} /> {t('gaps.none')}
                    </span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {s.gaps.map((g) => (
                        <span
                          key={g.code}
                          className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${GAP_TONE[g.code]}`}
                          title={t('gaps.blocks', { models: g.models.join(', ') })}
                        >
                          {t(`gaps.${g.code}`)}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <ChevronRight size={14} className="t-faint inline" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <div className="p-6 text-center text-sm t-muted">{t('loading')}</div>}
        {/* Si la carga falló no se puede afirmar nada del censo: «todavía no hay
            fichas» sería una conclusión que nadie ha podido comprobar. */}
        {!loading && !error && items.length === 0 && (
          <div className="p-6 text-center text-sm t-muted">
            {onlyIncomplete ? t('empty.noIncomplete') : t('empty.none')}
          </div>
        )}
      </div>

      {(editing || creating) && (
        <SupplierModal
          supplier={editing}
          canWrite={canWrite}
          onClose={() => {
            setEditing(null)
            setCreating(false)
          }}
          onSaved={() => {
            setEditing(null)
            setCreating(false)
            void reload()
          }}
        />
      )}
    </div>
  )
}

interface FormState {
  name: string
  isCompany: boolean
  firstName: string
  lastName1: string
  lastName2: string
  nif: string
  country: string
  provinceCode: string
  vatNumber: string
  address: string
  city: string
  postalCode: string
  establishedInTai: boolean
  reverseCharge: boolean
  retentionKey: string
  retentionSubkey: string
  active: boolean
  notes: string
}

function initialForm(s: SupplierDto | null): FormState {
  return {
    name: s?.name ?? '',
    isCompany: s?.isCompany ?? true,
    firstName: s?.firstName ?? '',
    lastName1: s?.lastName1 ?? '',
    lastName2: s?.lastName2 ?? '',
    nif: s?.nif ?? '',
    country: s?.country ?? 'ES',
    provinceCode: s?.provinceCode ?? '',
    vatNumber: s?.vatNumber ?? '',
    address: s?.address ?? '',
    city: s?.city ?? '',
    postalCode: s?.postalCode ?? '',
    establishedInTai: s?.establishedInTai ?? true,
    reverseCharge: s?.reverseCharge ?? false,
    retentionKey: s?.retentionKey ?? '',
    retentionSubkey: s?.retentionSubkey ?? '',
    active: s?.active ?? true,
    notes: s?.notes ?? '',
  }
}

function SupplierModal({
  supplier,
  canWrite,
  onClose,
  onSaved,
}: {
  supplier: SupplierDto | null
  canWrite: boolean
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useTranslation(['suppliers', 'common'])
  const [form, setForm] = useState<FormState>(() => initialForm(supplier))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mergeSource, setMergeSource] = useState('')
  const [others, setOthers] = useState<SupplierDto[]>([])
  const isEdit = supplier !== null

  /**
   * Candidatas a unificar: TODAS las fichas, no las que el filtro deje ver.
   *
   * La pantalla se usa casi siempre con «Solo las que les falta algo» o con algo
   * escrito en el buscador, y entonces la ficha duplicada que se quiere retirar
   * normalmente no está en la lista: el bloque de unificar desaparecía, o
   * proponía candidatas que no eran. Las inactivas también entran, que son
   * justamente las que suelen sobrar.
   */
  useEffect(() => {
    if (!isEdit || !canWrite) return
    let vivo = true
    api
      .get<SupplierListResponse>('/suppliers?inactive=true')
      .then((r) => {
        if (vivo) setOthers(r.items.filter((s) => s.id !== supplier!.id))
      })
      .catch(() => {
        // Sin candidatas el bloque no se pinta: unificar es opcional y no puede
        // impedir que se guarde la ficha.
        if (vivo) setOthers([])
      })
    return () => {
      vivo = false
    }
  }, [isEdit, canWrite, supplier])

  function upd<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((f) => ({ ...f, [k]: v }))
  }

  /**
   * El código postal escribe la provincia EN EL FORMULARIO, no en el servidor.
   *
   * POR QUÉ AQUÍ Y NO AL GUARDAR: si lo dedujera el servidor por detrás, una
   * provincia equivocada —un código postal del almacén en vez del domicilio
   * fiscal, un OCR torcido— sería imborrable: se volvería a poner sola en cada
   * guardado. Escribiéndola aquí, la persona la ve, la cambia o la vacía, y lo
   * que se guarda es lo que está en pantalla. Y son esos dos dígitos los que
   * viajan a las posiciones 77-78 del fichero del 347.
   *
   * Solo rellena el hueco: nunca pisa una provincia ya elegida.
   */
  const postalCode = form.postalCode
  const country = form.country
  useEffect(() => {
    const digits = postalCode.replace(/\D/g, '')
    if (digits.length !== 5 || country !== 'ES') return
    const code = digits.slice(0, 2)
    const valido = AEAT_PROVINCES.some(
      (p) => p.code === code && p.code !== AEAT_PROVINCE_NON_RESIDENT
    )
    if (!valido) return
    setForm((f) => (f.provinceCode ? f : { ...f, provinceCode: code }))
  }, [postalCode, country])

  // Un tercero de fuera de España no puede llevar provincia española: el diseño
  // del 347 manda el 99 para el no residente sin establecimiento permanente.
  // Basta con que el país no sea España: si se exigiera además la marca de «no
  // establecido» —que nace en false—, un proveedor alemán recién creado se
  // quedaría sin ninguna salida para el hueco de la provincia, porque el
  // desplegable solo ofrece provincias españolas.
  const suggestNonResident =
    form.country !== 'ES' && form.provinceCode !== AEAT_PROVINCE_NON_RESIDENT

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const payload = {
        name: form.name.trim(),
        isCompany: form.isCompany,
        firstName: form.firstName.trim() || null,
        lastName1: form.lastName1.trim() || null,
        lastName2: form.lastName2.trim() || null,
        nif: form.nif.trim() || null,
        country: form.country.trim().toUpperCase(),
        provinceCode: form.provinceCode || null,
        vatNumber: form.vatNumber.trim() || null,
        address: form.address.trim() || null,
        city: form.city.trim() || null,
        postalCode: form.postalCode.trim() || null,
        establishedInTai: form.establishedInTai,
        reverseCharge: form.reverseCharge,
        retentionKey: form.retentionKey || null,
        retentionSubkey: form.retentionSubkey || null,
        active: form.active,
        notes: form.notes.trim() || null,
      }
      if (isEdit) await api.patch(`/suppliers/${supplier!.id}`, payload)
      else await api.post('/suppliers', payload)
      onSaved()
    } catch (err) {
      // `apiErrorMessage` solo lee `body.error`, y en un formulario de veinte
      // campos un «Datos inválidos» pelado no dice qué hay que corregir: el
      // primer error de campo de zod sí.
      const campo =
        err instanceof ApiError
          ? Object.values(
              (err.body as { issues?: { fieldErrors?: Record<string, string[]> } })?.issues
                ?.fieldErrors ?? {}
            )[0]?.[0]
          : undefined
      setError(campo ?? apiErrorMessage(err, t('errors.save')))
    } finally {
      setSubmitting(false)
    }
  }

  async function review() {
    try {
      await api.post(`/suppliers/${supplier!.id}/review`, {})
      toast.success(t('form.reviewed'))
      onSaved()
    } catch (err) {
      toast.error(apiErrorMessage(err, t('errors.save')))
    }
  }

  async function merge(sourceId: string) {
    const source = others.find((s) => s.id === sourceId)
    if (!source) return
    const ok = await confirmDialog({
      title: t('merge.title'),
      message: t('merge.confirm', { source: source.name, target: supplier!.name }),
      tone: 'danger',
    })
    if (!ok) return
    try {
      const r = await api.post<{ expensesMoved: number }>(`/suppliers/${supplier!.id}/merge`, {
        sourceId,
      })
      setMergeSource('')
      toast.success(t('merge.done', { count: r.expensesMoved, target: supplier!.name }))
      onSaved()
    } catch (err) {
      toast.error(apiErrorMessage(err, t('errors.save')))
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? supplier!.name : t('form.newTitle')}
      description={t('form.description')}
      maxWidth="max-w-3xl"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        {error && <div className="text-sm text-rose-600">{error}</div>}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block sm:col-span-2">
            <span className="text-xs t-muted">{t('form.name')}</span>
            <input
              value={form.name}
              onChange={(e) => upd('name', e.target.value)}
              required
              disabled={!canWrite}
              className={inputCls}
            />
          </label>

          <label className="flex items-center gap-2 text-sm t-secondary sm:col-span-2">
            <input
              type="checkbox"
              checked={!form.isCompany}
              onChange={(e) => upd('isCompany', !e.target.checked)}
              disabled={!canWrite}
            />
            {t('form.isPerson')}
          </label>

          {/* Apellidos separados: el 347 y el 190 exigen «primer apellido, un
              espacio, segundo apellido, un espacio y el nombre completo». */}
          {!form.isCompany && (
            <>
              <label className="block">
                <span className="text-xs t-muted">{t('form.lastName1')}</span>
                <input
                  value={form.lastName1}
                  onChange={(e) => upd('lastName1', e.target.value)}
                  disabled={!canWrite}
                  className={inputCls}
                />
              </label>
              <label className="block">
                <span className="text-xs t-muted">{t('form.lastName2')}</span>
                <input
                  value={form.lastName2}
                  onChange={(e) => upd('lastName2', e.target.value)}
                  disabled={!canWrite}
                  className={inputCls}
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-xs t-muted">{t('form.firstName')}</span>
                <input
                  value={form.firstName}
                  onChange={(e) => upd('firstName', e.target.value)}
                  disabled={!canWrite}
                  className={inputCls}
                />
                <span className="text-[11px] t-faint">{t('form.firstNameHelp')}</span>
              </label>
            </>
          )}

          <label className="block">
            <span className="text-xs t-muted">{t('form.nif')}</span>
            <input
              value={form.nif}
              onChange={(e) => upd('nif', e.target.value)}
              placeholder="B12345678"
              disabled={!canWrite}
              className={`${inputCls} font-mono`}
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">{t('form.vatNumber')}</span>
            <input
              value={form.vatNumber}
              onChange={(e) => upd('vatNumber', e.target.value)}
              placeholder="IE4276970QH"
              disabled={!canWrite}
              className={`${inputCls} font-mono`}
            />
          </label>

          <label className="block">
            <span className="text-xs t-muted">{t('form.country')}</span>
            <input
              value={form.country}
              onChange={(e) => upd('country', e.target.value.toUpperCase().slice(0, 2))}
              maxLength={2}
              required
              pattern="[A-Z]{2}"
              title={t('form.countryHelp')}
              disabled={!canWrite}
              className={`${inputCls} font-mono`}
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">{t('form.provinceCode')}</span>
            <select
              value={form.provinceCode}
              onChange={(e) => upd('provinceCode', e.target.value)}
              disabled={!canWrite}
              className={inputCls}
            >
              <option value="">{t('form.provinceEmpty')}</option>
              {AEAT_PROVINCES.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.code} · {p.name}
                </option>
              ))}
            </select>

            {suggestNonResident && (
              <button
                type="button"
                onClick={() => upd('provinceCode', AEAT_PROVINCE_NON_RESIDENT)}
                className="text-[11px] text-cyan-700 dark:text-cyan-300 underline text-left"
              >
                {t('form.provinceNonResident')}
              </button>
            )}
          </label>

          <label className="block sm:col-span-2">
            <span className="text-xs t-muted">{t('form.address')}</span>
            <input
              value={form.address}
              onChange={(e) => upd('address', e.target.value)}
              disabled={!canWrite}
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">{t('form.postalCode')}</span>
            <input
              value={form.postalCode}
              onChange={(e) => upd('postalCode', e.target.value)}
              disabled={!canWrite}
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">{t('form.city')}</span>
            <input
              value={form.city}
              onChange={(e) => upd('city', e.target.value)}
              disabled={!canWrite}
              className={inputCls}
            />
          </label>
        </div>

        {/* IVA: establecido e inversión del sujeto pasivo. */}
        <fieldset className="surface-deep p-3 rounded-lg space-y-2">
          <legend className="text-xs font-semibold t-secondary px-1">{t('form.vatSection')}</legend>
          <label className="flex items-start gap-2 text-sm t-secondary">
            <input
              type="checkbox"
              checked={!form.establishedInTai}
              onChange={(e) => upd('establishedInTai', !e.target.checked)}
              disabled={!canWrite}
              className="mt-1"
            />
            <span>
              {t('form.notEstablished')}
              <span className="block text-[11px] t-faint">{t('form.notEstablishedHelp')}</span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm t-secondary">
            <input
              type="checkbox"
              checked={form.reverseCharge}
              onChange={(e) => upd('reverseCharge', e.target.checked)}
              disabled={!canWrite}
              className="mt-1"
            />
            <span>
              {t('form.reverseCharge')}
              <span className="block text-[11px] t-faint">{t('form.reverseChargeHelp')}</span>
            </span>
          </label>
          {!form.establishedInTai && (
            <p className="text-[11px] text-cyan-700 dark:text-cyan-300">{t('form.ispActive')}</p>
          )}
          {/* Canarias, Ceuta y Melilla están FUERA del territorio de aplicación
              del impuesto (art. 3.Dos.1.º LIVA). Un proveedor de Las Palmas con
              NIF español parece establecido y no lo está: sus servicios a la
              península llevan inversión del sujeto pasivo. Nada más lo delata,
              porque el NIF es español y la provincia es válida. */}
          {form.establishedInTai && FUERA_DEL_TAI.includes(form.provinceCode) && (
            <p className="text-[11px] text-amber-700 dark:text-amber-300">
              {t('form.outsideTaiWarning', { provincia: provinceName(form.provinceCode) })}
            </p>
          )}
        </fieldset>

        {/* Retención: clave y subclave del 190. */}
        <fieldset className="surface-deep p-3 rounded-lg space-y-2">
          <legend className="text-xs font-semibold t-secondary px-1">
            {t('form.retentionSection')}
          </legend>
          <p className="text-[11px] t-faint">{t('form.retentionHelp')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs t-muted">{t('form.retentionKey')}</span>
              <select
                value={form.retentionKey}
                onChange={(e) => {
                  upd('retentionKey', e.target.value)
                  // Cambiar de clave invalida la subclave: son listas distintas.
                  if (e.target.value !== form.retentionKey) upd('retentionSubkey', '')
                }}
                disabled={!canWrite}
                className={inputCls}
              >
                <option value="">{t('form.retentionNone')}</option>
                {MODEL_190_KEYS.map((k) => (
                  <option key={k.key} value={k.key}>
                    {k.key} · {k.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs t-muted">{t('form.retentionSubkey')}</span>
              <select
                value={form.retentionSubkey}
                onChange={(e) => upd('retentionSubkey', e.target.value)}
                disabled={!canWrite || !form.retentionKey}
                className={inputCls}
              >
                <option value="">{t('form.subkeyEmpty')}</option>
                {/* Las subclaves salen de la clave elegida. Si una clave se
                    ofreciera sin subclaves, su ficha no podría cerrar nunca el
                    hueco de la subclave: incompleta para siempre. */}
                {subkeysForKey(form.retentionKey).map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.key} · {s.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </fieldset>

        <label className="block">
          <span className="text-xs t-muted">{t('form.notes')}</span>
          <textarea
            value={form.notes}
            onChange={(e) => upd('notes', e.target.value)}
            rows={2}
            disabled={!canWrite}
            className={inputCls}
          />
        </label>

        <label className="flex items-center gap-2 text-sm t-secondary">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => upd('active', e.target.checked)}
            disabled={!canWrite}
          />
          {t('form.active')}
        </label>

        {/* Las claves por las que se reconoce el tercero: es la trazabilidad de
            por qué dos NIF escritos distinto son el mismo proveedor. */}
        {isEdit && supplier!.keys.length > 0 && (
          <div className="text-[11px] t-faint">
            {t('form.keys')}: <span className="font-mono">{supplier!.keys.join(' · ')}</span>
          </div>
        )}

        {/* Unificar con otra ficha del mismo tercero. */}
        {/* ☠️ UNIFICAR NO CUELGA DEL `onChange` DEL DESPLEGABLE. En Chrome de
            Windows y en Firefox, las flechas sobre un select cerrado cambian el
            valor y lanzan `change` en cada pulsación: una sola flecha abría la
            confirmación de una acción destructiva sobre una ficha que el usuario
            no había visto. Se elige primero y se pulsa después. */}
        {isEdit && canWrite && others.length > 0 && (
          <div className="space-y-1.5">
            <span className="text-xs t-muted">{t('merge.label')}</span>
            <div className="flex items-center gap-2">
              <select
                value={mergeSource}
                onChange={(e) => setMergeSource(e.target.value)}
                className={inputCls}
              >
                <option value="">{t('merge.placeholder')}</option>
                {others.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} {s.nif ? `· ${s.nif}` : ''} ({s.expenseCount})
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={!mergeSource}
                onClick={() => void merge(mergeSource)}
                className="px-3 py-2 rounded-lg text-sm whitespace-nowrap t-secondary border border-base bg-hover disabled:opacity-40"
              >
                {t('merge.button')}
              </button>
            </div>
            <span className="block text-[11px] t-faint">{t('merge.help')}</span>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 pt-2">
          <div>
            {isEdit && canWrite && (
              <button
                type="button"
                onClick={() => void review()}
                className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base bg-hover flex items-center gap-1.5"
              >
                <ShieldCheck size={14} /> {t('form.review')}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base bg-hover"
            >
              {t('common:actions.cancel')}
            </button>
            {canWrite && (
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-amber-600 to-amber-700 disabled:opacity-50"
              >
                {submitting ? t('form.saving') : t('common:actions.save')}
              </button>
            )}
          </div>
        </div>
      </form>
    </Modal>
  )
}
